import { FlatListWithTabs } from 'app/atoms/flatlist-with-tabs';
import { NothingSelectedErrorModal } from 'app/components/nothing-selected-error-modal';
import { PopularItemSelectionList } from 'app/components/popular-item-selection-list';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';

import { newListingSourceAtom } from 'app/atoms';
import { CategoryIcons, CategoryTab } from 'app/design/categories/tab';
import { images } from 'app/design/images';
import { Button, PressableType } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { useQuickOffers } from 'app/hooks/use-quickoffers';
import { Event, track } from 'app/lib/analytics';
import { getI18nLanguage } from 'app/lib/language';
import { usePopularItemsByCategory, type Category } from 'app/network/category';
import type { NavigationScreen } from 'app/types/navigation';
import { useAtomValue } from 'jotai';
import { useEffect, useEffectEvent, useState } from 'react';
import { useForm as useRHForm, type ControllerRenderProps, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, View } from 'react-native';
import { usePageTitle } from 'app/utils/web-helpers';
import { type PopularItem } from 'app/types/category';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

type Dictionary<T> = Record<string, T>;

const ChooseWebScreenComponent: NavigationScreen<'choose'> = ({ navigation }) => {
  const { t } = useTranslation('choose');

  const i18nLanguage = getI18nLanguage();

  const { control, handleSubmit } = useRHForm({
    mode: 'onChange',
  });

  const setPageTitle = usePageTitle('choose');
  useEffect(() => setPageTitle('headerTitle'), [setPageTitle]);

  const newListingSource = useAtomValue(newListingSourceAtom);
  const trackOpened = useEffectEvent(() => {
    track(Event.QuickOffersOpened, { source: newListingSource });
  });
  useEffect(() => trackOpened(), []);

  // Load data from API
  const categoriesQuery = usePopularItemsByCategory();
  const [selectedItemIds, setSelectedItemIds] = useState<Set<PopularItem['id']>>(() => new Set());

  // Parse received data into tabs & categoriesByIcon
  const { tabs, categories } = (() => {
    const _tabs: Record<string, React.JSX.Element | null> = {};
    const _categories: Dictionary<Category> = {};
    const firstCategoryByColor = new Map<string, Category>();
    for (const cat of categoriesQuery.data ?? []) {
      _tabs[cat.name.en] = null;
      const category = {
        ...cat,
        popularItems: cat.popularItems?.map((item) => ({ ...item, selected: selectedItemIds.has(item.id) })),
      };
      if (!category.colorDark) {
        category.colorDark = theme.colors['text.primary'];
      }
      if (category.colorLight.toLowerCase() === '#ebbb4d') {
        // Multiple Halloween categories (same colorLight) = invert the colors of the 2nd category
        const firstCatWithThisColor = firstCategoryByColor.get(cat.colorLight) ?? cat;
        if (firstCatWithThisColor.icon !== cat.icon) {
          category.colorLight = firstCatWithThisColor.colorDark;
          category.colorDark = firstCatWithThisColor.colorLight;
        }
      }
      if (!firstCategoryByColor.has(cat.colorLight)) firstCategoryByColor.set(cat.colorLight, cat);
      _categories[cat.name.en] = category;
    }
    return { tabs: _tabs, categories: _categories };
  })();

  const formIsValid = selectedItemIds.size > 0;

  // Toggle (on/off) an item
  const toggleSelectedItem = (popItem: PopularItem, field: ControllerRenderProps<FieldValues, `row_${string}`>) => {
    const selected = !selectedItemIds.has(popItem.id);
    setSelectedItemIds((current) => {
      const next = new Set(current);
      if (selected) next.add(popItem.id);
      else next.delete(popItem.id);
      return next;
    });
    field.onChange(selected);
  };

  const [showErrorModal, setShowErrorModal] = useState(false);

  const { goToNext } = useQuickOffers();

  const onSubmit = handleSubmit(() => {
    const popularItems: PopularItem[] = [];
    for (const tab of Object.keys(tabs)) {
      const cat = categories[tab];
      for (const popItem of cat!.popularItems!) {
        if (popItem.selected) {
          popularItems.push({ ...popItem });
        }
      }
    }
    goToNext({ selectedItems: popularItems });
  });

  const onPress = async () => {
    if (!formIsValid) {
      setShowErrorModal(true);
      return;
    }
    await onSubmit();
  };

  if (!categoriesQuery.data?.length) {
    // @TODO Loading view
    return <View />;
  }

  const overflow = 0;

  const itemMargin = theme.spacing['spacing.general.x4'];
  const leftPanelWidth = 295;
  const scrollableWidth = leftPanelWidth - 2 * itemMargin;
  const itemWidth = scrollableWidth / (1 + overflow);

  const renderPopularItemsList = (item: string) => {
    return (
      <View
        style={{
          width: itemWidth,
          margin: theme.spacing['spacing.general.x2'],
          borderRadius: theme.borderRadii['radius.xl'],
          backgroundColor: categories[item]!.colorLight,
          paddingHorizontal: 20,
          paddingVertical: 20,
        }}
      >
        <Text
          fontWeight="700"
          marginBottom="spacing.general.x2"
          style={{ color: categories[item]!.colorDark }}
          textAlign="center"
          variant="bodyLarge"
        >
          {categories[item]!.name[i18nLanguage]}
        </Text>
        <ScrollView
          style={{ flex: 1, maxHeight: 250 }}
          contentContainerStyle={{
            paddingBottom: theme.spacing['spacing.general.x8'],
            flexGrow: 1,
            minHeight: 300,
          }}
        >
          <PopularItemSelectionList
            control={control}
            items={categories[item]!.popularItems}
            language={i18nLanguage}
            onToggle={toggleSelectedItem}
          />
        </ScrollView>
      </View>
    );
  };

  const tabsDefinition = Object.keys(tabs).map((tab) => ({
    image: <CategoryTab category={CategoryIcons[categories[tab]!.icon as keyof typeof CategoryIcons]} />,
    selectedImage: (
      <CategoryTab
        category={CategoryIcons[categories[tab]!.icon as keyof typeof CategoryIcons]}
        color={categories[tab]!.colorDark}
      />
    ),
    selectedColor: categories[tab]!.colorLight,
    value: tab,
  }));

  const tabsElements: Record<string, React.JSX.Element> = {};
  for (const cat of Object.keys(tabs)) {
    tabsElements[cat] = renderPopularItemsList(cat);
  }

  return (
    <Row backgroundColor="surface.default" flex={1}>
      <Column gap="spacing.general.x5" flex={1} justifyContent="center">
        <View>
          <Text
            fontWeight="700"
            marginBottom="spacing.general.x2"
            style={{ marginHorizontal: 16 + 30 }}
            textAlign="center"
            variant="headerXSmall"
          >
            {t('headerTitle')}
          </Text>
          <Text
            marginBottom="spacing.general.x2"
            style={{ marginHorizontal: 16 + 30 }}
            textAlign="center"
            variant="headerXSmall"
          >
            {t('headerText')}
          </Text>

          <FlatListWithTabs
            initialTab={Object.keys(tabs)[0] ?? ''}
            marginBelowTabs={theme.spacing['spacing.general.x5']}
            overflow={overflow}
            screenName="choose"
            tabs={tabsElements}
            tabsDefinition={tabsDefinition}
            fixedContainerWidth={leftPanelWidth}
          />

          <Button
            marginHorizontal="spacing.general.x4"
            marginTop="spacing.general.x4"
            onPress={() => void onPress()}
            variant="primary"
          >
            <Text color="surface.default" fontWeight={formIsValid ? '700' : '500'} textAlign="center">
              {t('nextStep')}
            </Text>
          </Button>

          <Button
            onPress={() => {
              navigation.getParent()?.reset({
                routes: [{ name: 'main' }],
              });
            }}
            pressableType={PressableType.GestureHandler}
            marginTop="spacing.general.x4"
            variant="plain"
          >
            <Text textDecorationLine="underline" flex={1} textAlign="center" variant="bodyBase">
              {t('later')}
            </Text>
          </Button>

          <NothingSelectedErrorModal
            buttonLabel={t('nothingSelectedError.buttonText')}
            message={t('nothingSelectedError.text')}
            onClose={() => setShowErrorModal(false)}
            open={showErrorModal}
            title={t('nothingSelectedError.title')}
          />
        </View>
      </Column>
      <Column flex={1}>
        <PCImage flex={1} cachePolicy="memory-disk" source={images.web.quick_choose} />
      </Column>
    </Row>
  );
};

export const ChooseWebScreen: NavigationScreen<'choose'> = withErrorBoundary(ChooseWebScreenComponent);
